import React from 'react';
import PropTypes from 'prop-types';

import { SeatElements as ThroneSeatElements } from '@weezevent/throne';
import { useTranslation } from 'react-i18next';

const LegendItem = function LegendItem({ name, Image, count = null, isSocialDistancing = true, isSelected = false, onClick = () => {} }) {
    const { t } = useTranslation();

    if ((name === 'Social Distancing' && !isSocialDistancing) || !count) {
        return null;
    }

    const getKeyTranslate = name => {
        let _name = name.split(' ').join('');
        return `ticket.common.seating.${_name.charAt(0).toLowerCase() + _name.slice(1)}`;
    };

    return (
        <button className={`availability-legend-item ${isSelected ? 'availability-legend-item--selected' : ''}`} onClick={onClick}>
            <span className="availability-legend-item__label">
                <Image />
                <span className="availability-legend-item__text">{t(getKeyTranslate(name))}</span>
            </span>
            <span className="availability-legend-item__container-count">
                <span>{`x`}</span>
                <span className="availability-legend-item__count">{count || 0}</span>
            </span>
        </button>
    );
};

LegendItem.propTypes = {
    name: PropTypes.string.isRequired,
    Image: PropTypes.node.isRequired,
    count: PropTypes.number,
    isSelected: PropTypes.bool,
    onClick: PropTypes.func
};

export const SeatMapLegend = React.forwardRef(function SeatMapLegend({ seats = [], isSocialDistancing = true, selectedStates = [], onStateToggle = () => {} }, ref) {
    const { t } = useTranslation();

    const seatsCount = React.useMemo(() => {
        const count = {
            available: 0,
            booked: 0,
            reserved: 0,
            inProgress: 0,
            unavailable: 0,
            locked_for_social_distancing: 0,
            scanned: 0
        };

        if (!seats?.length) {
            return count;
        }

        seats.forEach(seat => {
            const bookingState = seat.booking_state || '';

            switch (bookingState) {
                case 'AVAILABLE':
                    count.available++;
                    break;

                case 'BOOKED':
                    if (seat.active_booking?.attendee?.scanned) {
                        count.scanned++;
                    } else {
                        count.booked++;
                    }
                    break;

                case 'BOOKED_BY_RESERVATION':
                case 'PARTIALLY_AVAILABLE':
                case 'PARTIALLY_BOOKED':
                    count.reserved++;
                    break;

                case 'BOOKING_IN_PROGRESS':
                    count.inProgress++;
                    // TODO: Implement logic for distinguishing between reserved and inProgress
                    // but we need to have Throne ISO on this to display same icons on seatmap
                    // if (seat.active_booking?.batch_booking) {
                    //     count.reserved++;
                    // } else {
                    //     count.inProgress++;
                    // }
                    break;

                case 'PARTIALLY_BOOKING_IN_PROGRESS':
                    count.inProgress++;
                    break;

                case 'UNAVAILABLE':
                    count.unavailable++;
                    break;

                case 'LOCKED_FOR_SOCIAL_DISTANCING':
                case 'LOCK_FOR_SOCIAL_DISTANCING':
                    count.locked_for_social_distancing++;
                    break;

                default:
                    console.error(`[SEAT LEGEND] Unclassified seat state: ${bookingState}`);
                    break;
            }
        });

        return count;
    }, [seats]);

    return (
        <div className="willy-availability-legend">
            <div className="willy-availability-legend-title">{t('ticket.swapSeat.legendZone')}</div>
            <div ref={ref} className="willy-availability-legend-list">
                <LegendItem
                    name="Available"
                    Image={ThroneSeatElements.available}
                    count={seatsCount.available}
                    isSelected={selectedStates.includes('available')}
                    onClick={() => onStateToggle('available')}
                />
                <LegendItem
                    name="Scanned"
                    Image={ThroneSeatElements.scanned}
                    count={seatsCount.scanned}
                    isSelected={selectedStates.includes('scanned')}
                    onClick={() => onStateToggle('scanned')}
                />
                <LegendItem
                    name="Booked"
                    Image={ThroneSeatElements.booked}
                    count={seatsCount.booked}
                    isSelected={selectedStates.includes('booked')}
                    onClick={() => onStateToggle('booked')}
                />
                <LegendItem
                    name="Reserved"
                    Image={ThroneSeatElements.booked_by_reservation}
                    count={seatsCount.reserved}
                    isSelected={selectedStates.includes('reserved')}
                    onClick={() => onStateToggle('reserved')}
                />
                <LegendItem
                    name="In Progress"
                    Image={ThroneSeatElements.booking_in_progress}
                    count={seatsCount.inProgress}
                    isSelected={selectedStates.includes('inProgress')}
                    onClick={() => onStateToggle('inProgress')}
                />
                <LegendItem
                    name="Unavailable"
                    Image={ThroneSeatElements.unavailable}
                    count={seatsCount.unavailable}
                    isSelected={selectedStates.includes('unavailable')}
                    onClick={() => onStateToggle('unavailable')}
                />
                <LegendItem
                    name="Social Distancing"
                    Image={ThroneSeatElements.locked_for_social_distancing}
                    count={seatsCount.locked_for_social_distancing}
                    isSocialDistancing={isSocialDistancing}
                    isSelected={selectedStates.includes('socialDistancing')}
                    onClick={() => onStateToggle('socialDistancing')}
                />
            </div>
        </div>
    );
});

SeatMapLegend.propTypes = {
    seats: PropTypes.array,
    selectedStates: PropTypes.array,
    onStateToggle: PropTypes.func
};

export default ThroneSeatElements;
